import { useState } from 'react';
import type { Combatant } from '@sd/shared';
import { CONDITIONS } from './conditions.js';
import { PipBar, nextMarks } from './PipBar.js';
import { Icon, ICON_TYPES } from '../adventures/Icon.js';
import { iconTypeFor } from '../adventures/icons.js';

interface Props {
  combatant: Combatant;
  /** Встановлює нове абсолютне значення hp marks */
  onHpSet?: (newMarks: number) => void;
  onStressSet?: (newMarks: number) => void;
  onArmorSet?: (newMarks: number) => void;
  onConditionToggle?: (type: string, on: boolean) => void;
  onDelete?: () => void;
  /** GM-роли: action attack (Hope+Fear+attackMod) */
  onAttackRoll?: (modifier: number, label: string) => void;
  /** GM-роли: damage за формулою */
  onDamageRoll?: (formula: string, label: string, damageType?: string) => void;
  /** GM-only: змінити зовнішній вигляд токена (іконка/illustration/колір). */
  onAppearanceChange?: (patch: { icon?: string; illustration?: string; tokenColor?: string }) => void;
}

export function CombatantDetail({
  combatant: c,
  onHpSet, onStressSet, onArmorSet, onConditionToggle, onDelete,
  onAttackRoll, onDamageRoll, onAppearanceChange,
}: Props): JSX.Element {
  const initial = (c.name?.[0] ?? '?').toUpperCase();
  return (
    <article className={`detail-card ${c.kind}`}>
      <div className="dc-head">
        <div className="dc-portrait" style={{ position: 'relative', overflow: 'hidden' }}>
          {c.illustration ? (
            <img src={c.illustration} alt="" style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover' }} />
          ) : c.icon ? (
            <Icon type={iconTypeFor(c.icon)} size={36} stroke={1.6} />
          ) : (
            initial
          )}
        </div>
        <div className="dc-titles">
          <h3>{c.name}</h3>
          <div className="sub">
            {c.kind}{c.stats.difficulty !== undefined ? ` · DC ${c.stats.difficulty}` : ''}
            {c.hidden ? ' · прихований' : ''}
            {c.count > 1 ? ` · ×${c.count}` : ''}
          </div>
        </div>
        {onDelete && (
          <button
            onClick={onDelete}
            title="Видалити з зустрічі"
            style={{
              background: 'none', border: 'none', color: 'var(--blood-bright)',
              cursor: 'pointer', fontSize: 18, padding: 4,
            }}
          >×</button>
        )}
      </div>

      {/* Daggerheart marks: 0 = здоровий, max = downed */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginTop: 12 }}>
        <PipBarRow
          label="Шкода (HP)"
          marked={c.stats.hp}
          max={c.stats.hpMax}
          kind="hp"
          onSet={onHpSet}
          hint={c.stats.hp >= c.stats.hpMax ? 'ПОВАЛЕНИЙ' : c.stats.hp >= Math.ceil(c.stats.hpMax * 2 / 3) ? 'критично' : c.stats.hp >= Math.ceil(c.stats.hpMax / 3) ? 'поранений' : 'здоровий'}
        />
        <PipBarRow
          label="Стрес"
          marked={c.stats.stress}
          max={c.stats.stressMax}
          kind="stress"
          onSet={onStressSet}
          hint={c.stats.stress >= c.stats.stressMax ? 'ЗЛАМАНИЙ' : `${c.stats.stress}/${c.stats.stressMax}`}
        />
        {c.stats.armorMax > 0 && (
          <PipBarRow
            label="Броня (витрат)"
            marked={c.stats.armor}
            max={c.stats.armorMax}
            kind="armor"
            onSet={onArmorSet}
            hint={c.stats.armor >= c.stats.armorMax ? 'вся витрачена' : `${c.stats.armorMax - c.stats.armor} вільних`}
          />
        )}
      </div>

      {/* Solo-stats для ворогів */}
      {(c.kind === 'adversary' || c.stats.attackMod !== undefined) && (
        <div className="dc-stats" style={{ marginTop: 14 }}>
          {c.stats.thresholdMajor !== undefined && (
            <div className="dc-stat">
              <div className="lbl">Maj</div>
              <div className="val">{c.stats.thresholdMajor}</div>
            </div>
          )}
          {c.stats.thresholdSevere !== undefined && (
            <div className="dc-stat">
              <div className="lbl">Sev</div>
              <div className="val">{c.stats.thresholdSevere}</div>
            </div>
          )}
          {c.stats.attackMod !== undefined && (
            <div className="dc-stat">
              <div className="lbl">+Атк</div>
              <div className="val">+{c.stats.attackMod}</div>
            </div>
          )}
          <div className="dc-stat">
            <div className="lbl">Поз.</div>
            <div className="val" style={{ fontSize: 14 }}>
              {Math.floor(c.tokenX)},{Math.floor(c.tokenY)}
            </div>
          </div>
        </div>
      )}

      {/* Conditions */}
      <div className="conditions-row" style={{ marginTop: 12 }}>
        {CONDITIONS.map((def) => {
          const on = c.conditions.includes(def.type);
          return (
            <button
              key={def.type}
              className={`cond-btn ${on ? 'on' : ''}`}
              onClick={() => onConditionToggle?.(def.type, !on)}
              title={def.label}
            >{def.short}</button>
          );
        })}
      </div>

      {/* Attacks — клікабельні якщо є GM-callbacks */}
      {c.stats.attacks.length > 0 && (
        <div className="dc-block">
          <h4>Атаки {onAttackRoll && <span style={{ fontSize: 9, color: 'var(--text-dim)', textTransform: 'none', letterSpacing: 0, marginLeft: 6 }}>(клік — ролл)</span>}</h4>
          {c.stats.attacks.map((a, i) => (
            <div key={i} className="quick-row" style={{ marginTop: i === 0 ? 4 : 0 }}>
              <div>
                <div className="label">{a.name}{a.primary ? ' ✦' : ''}</div>
                {(a.range || a.damage) && (
                  <div className="info">
                    {a.range ?? ''}{a.range && a.damage ? ' · ' : ''}{a.damage ?? ''}{a.damageType ? ` ${a.damageType}` : ''}
                  </div>
                )}
              </div>
              {onAttackRoll ? (
                <button
                  className="quick-btn"
                  onClick={() => onAttackRoll(c.stats.attackMod ?? 0, `${c.name}: ${a.name}`)}
                  title={`d20${c.stats.attackMod ? ((c.stats.attackMod > 0 ? '+' : '') + c.stats.attackMod) : ''} на влучання`}
                >Атк</button>
              ) : <span />}
              {a.damage && onDamageRoll ? (
                <button
                  className="quick-btn dmg"
                  onClick={() => onDamageRoll(a.damage!, `${c.name}: ${a.name}`, a.damageType)}
                  title={`Roll ${a.damage}`}
                >{a.damage}</button>
              ) : <span style={{ fontFamily: 'Spline Sans Mono, monospace', color: 'var(--blood-bright)', fontSize: 11 }}>
                {a.damage}
              </span>}
            </div>
          ))}
        </div>
      )}

      {/* Features */}
      {c.stats.features.length > 0 && (
        <div className="dc-block">
          <h4>Особливості</h4>
          {c.stats.features.map((f, i) => (
            <div key={i} className="dc-feature">
              <span className="name">{f.name}</span>
              <div className="desc">{f.description}</div>
            </div>
          ))}
        </div>
      )}

      {c.stats.tactics && (
        <div className="dc-tactics"><strong>Тактика:</strong> {c.stats.tactics}</div>
      )}
      {c.stats.motivation && (
        <div className="dc-tactics" style={{ borderLeftColor: 'var(--blood-dim)' }}>
          <strong>Мотив:</strong> {c.stats.motivation}
        </div>
      )}

      {onAppearanceChange && (
        <AppearancePanel
          icon={c.icon}
          illustration={c.illustration}
          tokenColor={c.tokenColor}
          onChange={onAppearanceChange}
        />
      )}
    </article>
  );
}

// ── Appearance: іконка/illustration/колір токена ─────────────────────────
function AppearancePanel({ icon, illustration, tokenColor, onChange }: {
  icon?: string;
  illustration?: string;
  tokenColor?: string;
  onChange: (patch: { icon?: string; illustration?: string; tokenColor?: string }) => void;
}): JSX.Element {
  const [urlDraft, setUrlDraft] = useState(illustration ?? '');
  const [open, setOpen] = useState(false);
  const currentIcon = icon ?? '';
  return (
    <div className="dc-block">
      <h4>
        Вигляд токена
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          style={{
            float: 'right', background: 'none', border: 'none', cursor: 'pointer',
            color: 'var(--text-dim)', fontFamily: 'Spline Sans Mono, monospace', fontSize: 10,
            textTransform: 'uppercase', letterSpacing: '0.1em',
          }}
        >{open ? 'згорнути' : 'розгорнути'}</button>
      </h4>
      {open && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginTop: 6 }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 11, color: 'var(--text-dim)' }}>
            Колір
            <input
              type="color"
              value={tokenColor ?? '#cca766'}
              onChange={(e) => onChange({ tokenColor: e.target.value })}
              style={{ width: 36, height: 24, padding: 0, border: 'none', background: 'transparent', cursor: 'pointer' }}
            />
            <span style={{ fontFamily: 'Spline Sans Mono, monospace', fontSize: 10 }}>{tokenColor ?? '—'}</span>
          </label>

          <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 11, color: 'var(--text-dim)' }}>
            Іконка
            <select
              value={currentIcon}
              onChange={(e) => onChange({ icon: e.target.value || undefined })}
              style={{
                flex: 1, background: 'var(--bg-deep)', color: 'var(--text)',
                border: '1px solid var(--border)', borderRadius: 3, padding: '4px 6px',
                fontFamily: 'Spline Sans Mono, monospace', fontSize: 11,
              }}
            >
              <option value="">— (літера)</option>
              {ICON_TYPES.map((t) => (
                <option key={t} value={t}>{t}</option>
              ))}
            </select>
            <span style={{
              width: 28, height: 28, display: 'flex', alignItems: 'center', justifyContent: 'center',
              border: '1px solid var(--border)', borderRadius: 4, background: 'var(--bg-deep)', color: 'var(--gold)',
            }}>
              {currentIcon ? <Icon type={iconTypeFor(currentIcon)} size={20} stroke={1.6} /> : <span style={{ fontSize: 11, color: 'var(--text-dim)' }}>—</span>}
            </span>
          </label>

          <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 11, color: 'var(--text-dim)' }}>
            Картинка (URL)
            <input
              type="url"
              value={urlDraft}
              onChange={(e) => setUrlDraft(e.target.value)}
              onBlur={() => {
                const v = urlDraft.trim();
                if (v !== (illustration ?? '')) onChange({ illustration: v || undefined });
              }}
              placeholder="https://… або /adventures/…"
              style={{
                flex: 1, background: 'var(--bg-deep)', color: 'var(--text)',
                border: '1px solid var(--border)', borderRadius: 3, padding: '4px 6px',
                fontFamily: 'Spline Sans Mono, monospace', fontSize: 11,
              }}
            />
            {illustration && (
              <button
                type="button"
                onClick={() => { setUrlDraft(''); onChange({ illustration: undefined }); }}
                title="Прибрати картинку"
                style={{
                  background: 'none', border: '1px solid var(--border)', color: 'var(--blood-bright)',
                  cursor: 'pointer', padding: '2px 8px', borderRadius: 3, fontSize: 11,
                }}
              >×</button>
            )}
          </div>
          <div style={{ fontSize: 10, color: 'var(--text-dim)', fontStyle: 'italic' }}>
            Картинка має пріоритет над іконкою. Іконка — над літерою.
          </div>
        </div>
      )}
    </div>
  );
}

function PipBarRow({ label, marked, max, kind, onSet, hint }: {
  label: string;
  marked: number;
  max: number;
  kind: 'hp' | 'stress' | 'armor';
  onSet?: (n: number) => void;
  hint?: string;
}): JSX.Element {
  return (
    <div>
      <div style={{
        display: 'flex', justifyContent: 'space-between', alignItems: 'baseline',
        fontFamily: 'Spline Sans Mono, monospace', fontSize: 10,
        textTransform: 'uppercase', letterSpacing: '0.12em',
        color: 'var(--text-dim)', marginBottom: 4,
      }}>
        <span>{label}</span>
        <span style={{ fontFamily: 'Cormorant Garamond, serif', fontSize: 11, color: hint?.toUpperCase() === hint ? 'var(--blood-bright)' : 'var(--gold)' }}>
          {marked}/{max}{hint ? ` · ${hint}` : ''}
        </span>
      </div>
      <PipBar
        marked={marked}
        max={max}
        kind={kind}
        onToggle={onSet ? (i, wasMarked) => onSet(nextMarks(marked, i, wasMarked)) : undefined}
      />
    </div>
  );
}
